import { useState } from 'react';
import Head from 'next/head';
import { observer, inject } from 'mobx-react';
import Image from 'next/image';
import MainMenu from '../menus/MainMenu';
import TopMenu from '../menus/TopMenu';
import MiniCart from '../cart/MiniCart';
import MobileMenu from '../menus/MobileMenu';
import SearchIcon from '../svg/SearchIcon';
import MenuIcon from '../svg/MenuIcon';
import MobileSearch from './MobileSearch';
import Link from '@/components/common/Link';

const Header = inject('generalSettingsStore', 'studioStore')(observer(({ generalSettingsStore, studioStore, title = '' }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const topMenuItems = generalSettingsStore.headerMenuItems?.top_menu_items || [];
  const mainMenuItems = generalSettingsStore.headerMenuItems?.main_menu_items || [];
  const siteTitle = generalSettingsStore.siteTitle || 'Beaver Next';
  const tagline = generalSettingsStore.tagline || '';
  const logo = generalSettingsStore.logo || '';
  const favicon = generalSettingsStore.favicon || '';
  const primaryColor = studioStore?.settings?.colors?.primary_color;
  const backgroundOpacity = (parseFloat(studioStore?.settings?.header?.background_opacity) || 100) / 100;
  const fixedToTop = (studioStore?.settings?.header?.fixed_to_top ? 'fixed' : 'relative');
  const topMenuBgColor = (studioStore?.settings?.header?.top_menu?.background_color ?? 'transparent');

  return (
    <>
      <Head>
        <title>
          {`${String(siteTitle)}${tagline ? ` - ${String(tagline)}` : ''}${title ? ` - ${String(title)}` : ''}`}
        </title>
        <link rel="icon" href={favicon} />
        <link rel="apple-touch-icon" href={favicon} />
        <link rel="shortcut icon" href={favicon} />
      </Head>
      <header className={`${fixedToTop} z-50 top-0 left-0 w-full main-menu-background`}>
        <div className="2xl:container mx-auto flex items-center justify-between">
          {/* Mobile Menu and Search */}
          <div className="flex items-center lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="p-2 rounded-md hover:bg-gray-100"
              aria-label="Open menu"
            >
              <MenuIcon className="w-6 h-6" stroke={primaryColor} />
            </button>
            <button
              onClick={() => setIsMobileSearchOpen(true)}
              className="p-2 rounded-md hover:bg-gray-100"
              aria-label="Open search"
            >
              <SearchIcon className="w-6 h-6" stroke={primaryColor} />
            </button>
          </div>

          {/* Logo */}
          <div className="flex items-center h-20 md:h-36 p-3 min-w-[11rem]">
            <Link href="/" className="flex items-center justify-center h-full w-full">
              {logo?.url ? (
                <Image
                  src={logo?.url}
                  alt={logo?.alt ? logo?.alt : logo?.title}
                  width={logo?.meta?.width}
                  height={logo?.meta?.height}
                  className="max-h-16 md:max-h-32 w-auto lg:w-full"
                  priority
                />
              ) : (
                <span className="text-xl md:text-2xl font-bold">{siteTitle}</span>
              )}
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex flex-col justify-between h-36 w-full mx-0 xl:mx-4">
            <TopMenu items={topMenuItems} color={primaryColor} />
            <MainMenu items={mainMenuItems} />
          </div>

          {/* Cart */}
          <div className="flex items-center h-20 md:h-36 p-3">
            <div className="relative flex items-center justify-center h-full">
              <MiniCart />
            </div>
          </div>
        </div>

        {/* Mobile Search */}
        <MobileSearch
          isOpen={isMobileSearchOpen}
          onClose={() => setIsMobileSearchOpen(false)}
        />

        {/* Mobile Menu */}
        <MobileMenu
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          topMenuItems={topMenuItems}
          mainMenuItems={mainMenuItems}
        />

        {/* Background */}
        <div className="hidden md:flex flex-col justify-between h-36 w-full absolute top-0 left-0 z-[-1]" style={{ opacity: backgroundOpacity }}>
          <div className="h-2/5 bg-gray-300" style={{ backgroundColor: topMenuBgColor }}></div>
        </div>
      </header>
    </>
  );
}));

export default Header;